import _ from 'lodash';
import { ToggleTextPanel } from 'Components/molecules';
import { DEFAULT_ERROR_MSG, ERROR_VARIANT, INFO_VARIANT } from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { Typography } from 'Components/atoms';
import { AmpSettingDto, AmpSettingUpsertDto, AMP_SETTINGS, AuthRole } from '@ampsec/platform-client';
import { ChangeEvent, useContext, useEffect, useState, useMemo } from 'react';
import { useApiData } from 'Hooks/useApiData';
import { createSetting, getTenantSettings, updateSettings } from 'Apis/library';
import PageErrorHandlerAndLoaderWrapper from 'Wrappers/PageErrorHandlerAndLoaderWrapper';
import './styles.scss';
import { BrowserCoachingListView } from 'Components/organisms/BrowserCoachingListView';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { AiServiceConfigurationModal, AiServicesConfig } from 'Components/organisms/AiServiceConfigurations/AiServiceConfigurationModal';
import { DEFAULT_AI_SERVICES_CONFIG } from './data';
import { decodeAiServicesConfigFromApi, encodeAiServicesConfigForApi } from './browserCoachingConfigEncoding';
import { TrainingAndCoachingTabs } from 'Components/organisms/TrainingAndCoachingTabs';

const BrowserCoachingPage = () => {
  const { t } = useI18n();
  const { addToast } = useContext(ToastsContext);
  const auth = useSelector((state: RootState) => state.auth);
  const [browserCoachingEnabled, setBrowserCoachingEnabled] = useState(AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.defaultValue);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const isReadOnly = useMemo(() => ((auth.user.role as unknown) as AuthRole) !== AuthRole.OWNER, [auth]);

  const { data: tenantSettings, isLoading, refresh: refreshTenantSettings, error: error } =
    useApiData<{ [key: string]: AmpSettingDto }, { [key: string]: AmpSettingDto }>('TENANT_SETTINGS', getTenantSettings, (a) => a);

  const getAmpSettingValue = (settingKey: string): any => {
    if (!settingKey || (tenantSettings && _.isEmpty(tenantSettings))) {
      return true;
    }
    return tenantSettings && (tenantSettings as any)[settingKey]?.value;
  };


  const AI_SERVICES_CONFIG_ID = AMP_SETTINGS.tenant.BROWSER_COACHING_AI_SERVICES_CONFIG.id;

  const getAiServicesConfig = (): AiServicesConfig => {
    if (!tenantSettings || !AI_SERVICES_CONFIG_ID) {
      return DEFAULT_AI_SERVICES_CONFIG;
    }
    const setting = (tenantSettings as any)[AI_SERVICES_CONFIG_ID];
    const raw = setting?.value || DEFAULT_AI_SERVICES_CONFIG;
    return decodeAiServicesConfigFromApi(raw) ?? raw;
  };

  const handleSaveAiServicesConfig = async (newConfig: AiServicesConfig) => {
    if (!AI_SERVICES_CONFIG_ID) {
      return;
    }
    const payload = encodeAiServicesConfigForApi(newConfig);
    try {
      await updateSettings(getAmpSettingId(AI_SERVICES_CONFIG_ID), {
        id: getAmpSettingId(AI_SERVICES_CONFIG_ID),
        key: AI_SERVICES_CONFIG_ID,
        value: payload,
      } as AmpSettingUpsertDto);
      await refreshTenantSettings();
      addToast({ body: t('AI_SERVICE_CONFIGURATION_SAVED_SUCCESSFULLY'), severity: INFO_VARIANT });
    } catch (error) {
      addToast({ body: DEFAULT_ERROR_MSG, severity: ERROR_VARIANT });
      throw error;
    }
  };

  const getAmpSettingId = (settingKey: string) => {
    if (!settingKey || tenantSettings === undefined || tenantSettings === null) {
        return '';
    }
    return ((tenantSettings as any)[settingKey]?.id as string) || '';
  };

  const updateAmpSetting = async (settingId: string, value: any) => {
    if (!settingId) {
      return;
    }
    try {
        await updateSettings(getAmpSettingId(settingId), {
            id: getAmpSettingId(settingId),
            key: settingId,
            value: value,
        } as AmpSettingUpsertDto);
        if (settingId === AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.id) {
            setBrowserCoachingEnabled(value as boolean);
        }
    } catch (e) {
        addToast({ body: DEFAULT_ERROR_MSG, severity: ERROR_VARIANT });
    }
  };

  const createAmpSetting = async (settingId: string, value: any) => {
    if (tenantSettings && !Object.keys(tenantSettings)?.includes(settingId)) {
        try {
            const payload = encodeAiServicesConfigForApi(value);
            await Promise.all([
              createSetting(settingId, payload),
              refreshTenantSettings(),
            ]);
        } catch (e) {
            addToast({ body: DEFAULT_ERROR_MSG, severity: ERROR_VARIANT });
        }
    } else {
        if (settingId === AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.id) {
            setBrowserCoachingEnabled(value);
        }
    }
  };

  const handleToggleBrowserCoachingEnabled = async (ev: ChangeEvent<HTMLInputElement>) => {
    const settingKey = AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.id;
    const newValue = ev.target.checked;
    const existingSetting = Object.keys(tenantSettings as any).find((setting) => setting === settingKey);

    // Lazy Creation:
    // If the setting doesn't exist on the backend yet, we create it (POST).
    // If it does exist, we update it (PUT).
    if (existingSetting !== undefined) {
      await updateAmpSetting(settingKey, newValue);
    } else {
      await createAmpSetting(settingKey, newValue);
    }
  };

  useEffect(() => {
    if (!tenantSettings || isLoading || Object.keys(tenantSettings).length === 0) {
      return;
    }
    const existingSetting = Object.keys(tenantSettings as any).find((setting) => setting === AI_SERVICES_CONFIG_ID);

    // creation for AI services config
    if (
      existingSetting === undefined
    ) {
      createAmpSetting(AI_SERVICES_CONFIG_ID, DEFAULT_AI_SERVICES_CONFIG);
    }

    const enabled = getAmpSettingValue(AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.id);
    if (enabled === undefined) {
      createAmpSetting(AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.id, AMP_SETTINGS.tenant.BROWSER_COACHING_ENABLED.defaultValue);
    } else {
      setBrowserCoachingEnabled(enabled as boolean);
    }
  }, [tenantSettings]);

  return (
    <PageErrorHandlerAndLoaderWrapper
          error={error}
          render={() => {
            return (
              <div className="d-flex flex-column engagement-flow__content mt-4">
                <div className="engagement-flow__tab-cont d-flex justify-content-between mb-4">
                  <TrainingAndCoachingTabs />
                </div>
                <div className="bg-text-black p-3 gap-3 d-flex card">
                  <Typography variant="body5" color="text-high-emphasis">
                    {t('BROWSER_COACHING_PAGE_SUBTITLE_1')}
                  </Typography>
                  <Typography variant="body3" color="text-high-emphasis">
                    {t('BROWSER_COACHING_PAGE_SUBTITLE_2')}
                  </Typography>

                  <div id="enable-browser-coaching-container" className="d-flex flex-column gap-3 p-3">
                    <ToggleTextPanel
                      header={t('ENABLE_BROWSER_COACHING_LABEL')}
                      checked={browserCoachingEnabled}
                      onToggle={handleToggleBrowserCoachingEnabled}
                      disabled={isReadOnly}
                    />
                  </div>
                  { browserCoachingEnabled && (
                    <BrowserCoachingListView
                      disabled={isReadOnly}
                      onConfigureAiServices={() => setIsConfigModalOpen(true)}
                    />
                  )}
                  <AiServiceConfigurationModal
                    isOpen={isConfigModalOpen}
                    onClose={() => setIsConfigModalOpen(false)}
                    initialConfig={getAiServicesConfig()}
                    onSave={handleSaveAiServicesConfig}
                  />
                </div>
              </div>
            );
          }}
          isLoading={isLoading}
          handleRefresh={refreshTenantSettings}
      />
  );
};

export default BrowserCoachingPage;
